import { Alert, Button } from "@opowiedzo/ui";
import { useCustomMutation, useGetIdentity, useInvalidate, useOne } from "@refinedev/core";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { Badge } from "@/components/Badge";
import { Labeled, SelectInput, TextArea } from "@/components/Form";
import {
  BackLink,
  Card,
  CardNote,
  DefinitionList,
  DetailGrid,
  ErrorBox,
  Loading,
  PageHeader,
} from "@/components/Page";
import {
  CHANNEL_OPTIONS,
  channelLabel,
  familyRoleLabel,
  formatDate,
  formatDateTime,
  memoirStatus,
  planLabel,
  scheduleLabel,
} from "@/lib/format";
import type { AdminIdentity } from "@/providers/authProvider";
import type { MemoirRow } from "./list";

interface Schedule {
  frequency: string;
  weekday: number;
  hour: number;
  timezone: string;
  channel: string;
  paused: boolean;
  next_delivery_at: string | null;
}

interface Topic {
  topic_key: string;
  mode: string;
}

interface Member {
  user_id: string;
  name: string;
  email: string;
  role: string;
  joined_at: string | null;
}

interface NextQuestion {
  question: string | null;
  status: string;
}

export interface MemoirDetail extends MemoirRow {
  family_id: string;
  family_name: string | null;
  started_at: string | null;
  target_stories: number;
  storyteller_email: string | null;
  storyteller_phone: string | null;
  storyteller_channels: string | null;
  storyteller_birth_year: number | null;
  schedule: Schedule | null;
  topics: Topic[];
  members: Member[];
  next_question: NextQuestion;
}

const TOPIC_MODES: Record<string, string> = {
  ask: "pytamy",
  skip: "nie pytamy",
  ask_first: "najpierw pytamy narratora o zgodę",
};

const NEXT_QUESTION_HINTS: Record<string, string> = {
  queued: "Wysyłka już założona, czeka na termin.",
  planned: "Tak wypadnie z silnika doboru przy najbliższej wysyłce.",
  family_prompt: "Następne pytanie pochodzi od rodziny i idzie przed biblioteką.",
  exhausted: "Biblioteka pytań dla tej Opowieści jest wyczerpana.",
  no_storyteller: "Opowieść nie ma narratora, więc nie ma komu zadać pytania.",
};

export function MemoirShowPage() {
  const { id = "" } = useParams();
  const { query } = useOne<MemoirDetail>({ resource: "memoirs", id });
  const { data: identity } = useGetIdentity<AdminIdentity | null>();
  const memoir = query.data?.data;

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!memoir) return null;

  const status = memoirStatus(memoir.status);
  const schedule = memoir.schedule;

  return (
    <>
      <BackLink to="/opowiesci">Opowieści</BackLink>
      <PageHeader
        title={memoir.title}
        description={memoir.family_name ?? undefined}
        actions={<Badge tone={status.tone}>{status.label}</Badge>}
      />

      <DetailGrid
        main={
          <>
            <Card title="Jak idzie">
              <DefinitionList
                rows={[
                  [
                    "Odsetek odpowiedzi",
                    memoir.response_rate === null
                      ? "jeszcze nie wiadomo - nie poszło ani jedno pytanie"
                      : `${memoir.response_rate}% (${memoir.answers} z ${memoir.prompts_sent})`,
                  ],
                  ["Historie", `${memoir.stories_count} z ${memoir.target_stories}`],
                  ["Zdjęcia", String(memoir.photos_count)],
                  ["Nagrania", String(memoir.recordings_count)],
                  ["Ostatnia aktywność", formatDateTime(memoir.last_activity_at)],
                  ["Start", formatDate(memoir.started_at)],
                  ["Pakiet", planLabel(memoir.plan)],
                ]}
              />
              <CardNote>
                Zła dostarczalność poczty wygląda w tych liczbach identycznie jak brak chęci.
                Zanim wyciągniesz wniosek, sprawdź{" "}
                <Link
                  to={`/wysylki-pytan?memoir_id=${memoir.id}&failures_only=true`}
                  className="text-accent underline underline-offset-3 hover:text-accent-dark"
                >
                  błędy wysyłek tej Opowieści
                </Link>
                .
              </CardNote>
            </Card>

            <Card title="Harmonogram">
              {!schedule ? (
                <p className="text-base text-ink-soft">
                  Ta Opowieść nie ma harmonogramu - pytania nigdy z niej nie wyjdą.
                </p>
              ) : (
                <DefinitionList
                  rows={[
                    ["Rytm", scheduleLabel(schedule.weekday, schedule.hour, schedule.frequency)],
                    ["Strefa czasu", schedule.timezone],
                    ["Kanał", channelLabel(schedule.channel)],
                    [
                      "Następna wysyłka",
                      schedule.paused ? "wstrzymana" : formatDateTime(schedule.next_delivery_at),
                    ],
                  ]}
                />
              )}
            </Card>

            <Card title="Następne pytanie">
              <p className="text-lg text-ink">{memoir.next_question.question ?? "-"}</p>
              <p className="mt-2 text-sm text-ink-soft">
                {NEXT_QUESTION_HINTS[memoir.next_question.status] ?? memoir.next_question.status}
              </p>
            </Card>

            <Card title="Tematy delikatne">
              {memoir.topics.length === 0 ? (
                <p className="text-base text-ink-soft">
                  Rodzina nie włączyła żadnego tematu delikatnego. Domyślnie o nie nie pytamy.
                </p>
              ) : (
                <DefinitionList
                  rows={memoir.topics.map((topic) => [
                    topic.topic_key,
                    TOPIC_MODES[topic.mode] ?? topic.mode,
                  ])}
                />
              )}
            </Card>
          </>
        }
        side={
          <>
            <Card title="Narrator">
              <DefinitionList
                rows={[
                  ["Imię", memoir.storyteller_name ?? "-"],
                  ["E-mail", memoir.storyteller_email ?? "-"],
                  ["Telefon", memoir.storyteller_phone ?? "-"],
                  ["Kanały", channelLabel(memoir.storyteller_channels)],
                  [
                    "Rocznik",
                    memoir.storyteller_birth_year ? String(memoir.storyteller_birth_year) : "-",
                  ],
                ]}
              />
            </Card>

            <Card title="Rodzina">
              {memoir.members.length === 0 ? (
                <p className="text-base text-ink-soft">Brak członków rodziny.</p>
              ) : (
                <ul className="flex flex-col gap-2.5 text-base">
                  {memoir.members.map((member) => (
                    <li key={member.user_id}>
                      <Link
                        to={`/uzytkownicy/${member.user_id}`}
                        className="text-accent underline underline-offset-4 hover:text-accent-dark"
                      >
                        {member.name}
                      </Link>
                      <span className="block text-xs text-ink-soft">
                        {familyRoleLabel(member.role)} - {member.email}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <ActionsPanel
              memoir={memoir}
              allowed={(identity?.permissions ?? []).includes("memoirs.act")}
            />
          </>
        }
      />
    </>
  );
}

/**
 * Dlaczego ten kanał nie zadziała dla tego narratora. E-mail i SMS backend
 * odrzuca od razu, telefon zapisuje - i wtedy milczy dopiero wysyłka.
 */
function brakKontaktu(kanal: string, memoir: MemoirDetail): string | null {
  if (kanal === "email" && !memoir.storyteller_email) {
    return "Narrator nie ma adresu e-mail - zmiana na ten kanał nie przejdzie.";
  }
  if (kanal === "sms" && !memoir.storyteller_phone) {
    return "Narrator nie ma numeru telefonu - zmiana na ten kanał nie przejdzie.";
  }
  if (kanal === "phone" && !memoir.storyteller_phone) {
    return "Narrator nie ma numeru telefonu. Ustawienie się zapisze, ale nikt nigdzie nie zadzwoni.";
  }
  return null;
}

function ActionsPanel({ memoir, allowed }: { memoir: MemoirDetail; allowed: boolean }) {
  const invalidate = useInvalidate();
  const { mutate, mutation } = useCustomMutation();
  const [powod, setPowod] = useState("");
  const [kanal, setKanal] = useState(memoir.schedule?.channel ?? "email");

  // Backend wymaga powodu przy każdej z tych akcji - trafia do audytu.
  const gotowy = powod.trim().length >= 3 && !mutation.isPending;

  const przeszkoda = brakKontaktu(kanal, memoir);

  const akcja = (sciezka: string, dodatkowe: Record<string, unknown> = {}) =>
    mutate(
      {
        url: `/memoirs/${memoir.id}/${sciezka}`,
        method: "post",
        values: { reason: powod.trim(), ...dodatkowe },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setPowod("");
          invalidate({ resource: "memoirs", invalidates: ["detail", "list"], id: memoir.id });
        },
      },
    );

  if (!allowed) {
    return (
      <Card title="Akcje">
        <p className="text-base text-ink-soft">
          Twoja rola nie obejmuje zmian w Opowieści. Zgłoś prośbę do operacji razem z powodem.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Akcje">
      <div className="flex flex-col gap-3">
        <Labeled label="Powód (trafi do audytu)">
          <TextArea
            rows={2}
            value={powod}
            onChange={(event) => setPowod(event.target.value)}
            placeholder="np. rodzina prosi o przerwę na czas pobytu w szpitalu"
          />
        </Labeled>

        {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}

        {memoir.paused ? (
          <Button variant="primary" disabled={!gotowy} onClick={() => akcja("resume")}>
            Wznów pytania
          </Button>
        ) : (
          <Button variant="primary" disabled={!gotowy} onClick={() => akcja("pause")}>
            Wstrzymaj pytania
          </Button>
        )}

        <div className="flex items-end gap-2">
          <Labeled label="Kanał wysyłki" className="flex-1">
            <SelectInput value={kanal} onChange={(event) => setKanal(event.target.value)}>
              {CHANNEL_OPTIONS.map((opcja) => (
                <option key={opcja.value} value={opcja.value}>
                  {opcja.label}
                </option>
              ))}
            </SelectInput>
          </Labeled>
          <Button disabled={!gotowy} onClick={() => akcja("channel", { channel: kanal })}>
            Zmień
          </Button>
        </div>
        <CardNote>
          {przeszkoda ??
            "Kanał dotyczy pytań z harmonogramu. Telefon znaczy rozmowę z agentem głosowym zamiast wiadomości."}
        </CardNote>

        <Link
          to={`/wyslij-pytanie?memoir_id=${memoir.id}`}
          className="text-base text-accent underline underline-offset-4 hover:text-accent-dark"
        >
          Wyślij pytanie teraz, poza harmonogramem
        </Link>
        <CardNote>
          Osobny ekran: wybrane pytanie wybranym kanałem, od razu, ze statusem wysyłki na
          miejscu. Do sprawdzenia, czy kanał naprawdę dochodzi do narratora.
        </CardNote>

        <Button disabled={!gotowy} onClick={() => akcja("reroll")}>
          Przelosuj następne pytanie
        </Button>
        <CardNote>
          Odrzucone pytanie zostaje w kolejce wysyłek jako pominięte i nie wróci. Dobór jest
          deterministyczny, więc bez tego to samo pytanie wypadłoby ponownie.
        </CardNote>

        <Button
          disabled={!gotowy}
          onClick={() => akcja("family-completion", { enabled: !memoir.family_completion })}
        >
          {memoir.family_completion
            ? "Wyłącz tryb dokończenia przez rodzinę"
            : "Włącz tryb dokończenia przez rodzinę"}
        </Button>
        <CardNote>
          {memoir.family_completion
            ? "Pytania do narratora są wyciszone. Wyłączenie trybu samo ich nie wznawia - to osobna decyzja."
            : "Pytania do narratora zamilkną, a Opowieść zostanie otwarta, żeby rodzina dopisała resztę."}
        </CardNote>
      </div>
    </Card>
  );
}
